import { Card } from '@design/components/card/Card';
import semanticColorStyles from '@design/styles/SemanticColor.module.scss';
import { formatPercentageNumber } from '@terminal/widgets/earnings/utils/utils';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { RatioIndicator } from '../RatioIndicator';
import type {
  CalendarQuarterPeriod,
  EarningsPresentationVariant,
  GuidanceStat,
} from '../utils';
import styles from './ThemeKeyStats.module.scss';
import {
  type KeyStat,
  type SurpriseStat,
  toGuidanceKeyStat,
  toSurpriseKeyStat,
} from './utils';

export type { SurpriseStat } from './utils';

interface ThemeKeyStatsProps {
  variant?: EarningsPresentationVariant;
  activeReportingPeriod?: CalendarQuarterPeriod;
  reportedCount: number;
  totalCount: number;
  epsStat?: SurpriseStat;
  revenueStat?: SurpriseStat;
  guidanceStat?: GuidanceStat;
  median1DReaction?: number;
}

export const ThemeKeyStats = ({
  variant = 'page',
  activeReportingPeriod,
  reportedCount,
  totalCount,
  epsStat,
  revenueStat,
  guidanceStat,
  median1DReaction,
}: ThemeKeyStatsProps) => {
  const { t } = useTranslation(['widget']);
  const isDrawer = variant === 'drawer';
  const surpriseDecimalPlaces = isDrawer ? 0 : undefined;
  const reportedLabel =
    isDrawer && activeReportingPeriod
      ? t(
          'widget:themeOverview.tabs.earnings.drawer.companiesReportedForPeriod',
          {
            period: `${activeReportingPeriod.quarter} ${activeReportingPeriod.year}`,
          }
        )
      : t('widget:themeOverview.tabs.earnings.keyStats.reported');

  const keyStats: KeyStat[] = [
    {
      labelKey: 'widget:themeOverview.tabs.earnings.keyStats.reported',
      primaryText: `${reportedCount}`,
      secondaryText: `/${totalCount}`,
    },
    toSurpriseKeyStat(
      'widget:themeOverview.tabs.earnings.keyStats.eps',
      epsStat,
      surpriseDecimalPlaces
    ),
    toSurpriseKeyStat(
      'widget:themeOverview.tabs.earnings.keyStats.revenue',
      revenueStat,
      surpriseDecimalPlaces
    ),
    toGuidanceKeyStat(guidanceStat),
    {
      labelKey: 'widget:themeOverview.tabs.earnings.keyStats.median1DReaction',
      primaryText: formatPercentageNumber({
        value: median1DReaction,
        addPlus: true,
      }),
      primaryColor:
        median1DReaction === undefined || median1DReaction === 0
          ? undefined
          : median1DReaction > 0
            ? 'bullish'
            : 'bearish',
    },
  ];

  return (
    <div
      className={clsx(styles.root, { [styles.drawer]: isDrawer })}
      data-testid="theme-key-stats"
    >
      {keyStats.map((stat, index) => (
        <Card
          key={stat.labelKey}
          size={isDrawer ? 'small' : 'large'}
          className={styles.tile}
        >
          <span className={styles.label}>
            {index === 0 ? reportedLabel : t(stat.labelKey)}
          </span>
          <div className={styles.valueRow}>
            {stat.indicator &&
              (stat.ratio !== undefined ? (
                <RatioIndicator
                  className={styles.indicator}
                  indicator={stat.indicator}
                  ratio={stat.ratio}
                />
              ) : (
                <span
                  className={clsx(styles.indicator, styles.indicatorSolid, {
                    [semanticColorStyles.directionPositive]:
                      stat.indicator === 'bullish',
                    [semanticColorStyles.directionNegative]:
                      stat.indicator === 'bearish',
                  })}
                />
              ))}
            <p
              className={clsx(styles.valueText, {
                [styles.withSeparator]: stat.secondarySeparator,
              })}
            >
              <span
                className={clsx(styles.primaryText, {
                  [semanticColorStyles.directionPositive]:
                    stat.primaryColor === 'bullish',
                  [semanticColorStyles.directionNegative]:
                    stat.primaryColor === 'bearish',
                })}
              >
                {stat.primaryText}
              </span>
              {stat.secondaryText && (
                <>
                  {' '}
                  <span className={styles.secondaryText}>
                    {stat.secondarySeparator && isDrawer && '· '}
                    {stat.secondaryText}
                  </span>
                </>
              )}
            </p>
          </div>
        </Card>
      ))}
    </div>
  );
};
